import React, { useLayoutEffect } from 'react';
import type { ViewProps } from 'react-native';
import {
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import type { AddressType, MapProps } from '@sweetgreen/garnish';
import {
  AddressTypes,
  LocationCard,
  Map,
  ModalRow,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { LoadingAnimation, TransparentModal } from '@order/components';

import type { ModalStackChildProps } from '../../../../navigation/AppNavigation.props';
import type { AddressQuery } from '../../../DeliveryMenuScreen/GraphQL/DeliveryRestaurant.query.generated';
import { useAddressQuery } from '../../../DeliveryMenuScreen/GraphQL/DeliveryRestaurant.query.generated';
import type { ReorderRestaurantQuery } from '../../GraphQL/ReorderConfirmLocation.generated';
import {
  useCancelReorder,
  useChangeLocation,
  useConflictReview,
  useDismissReorder,
  useReorderState,
} from '../../hooks';
import {
  ModalFooter,
  ModalHeader,
  ReorderTitle,
  useReorderRightIcon,
} from './ReorderConfirmLocation.components';
import { useReorderConfirmLocationData } from './ReorderConfirmLocation.data';

export const ReorderConfirmLocation = ({
  navigation,
}: ModalStackChildProps<'ReorderConfirmLocation'>) => {
  useDismissReorder('locationConfirmation');

  const {
    currentStep,
    reordering,
    restaurantId,
    orderType,
    deliveryOrderDetail,
  } = useReorderState();
  const cancelReorder = useCancelReorder();
  const changeLocation = useChangeLocation();
  const conflictReview = useConflictReview();

  const { fetching, region, pins, restaurant } = useReorderConfirmLocationData(
    restaurantId,
    orderType,
  );

  if (currentStep !== 'locationConfirmation' && Platform.OS === 'web')
    return null;

  if (!restaurant) return <LoadingAnimation />;

  return (
    <ReorderConfirmLocationContent
      navigation={navigation}
      fetching={fetching}
      orderType={orderType}
      restaurant={restaurant}
      region={region}
      pins={pins}
      isLoading={reordering}
      cancelReorder={cancelReorder}
      onClose={cancelReorder}
      onContinue={conflictReview}
      onMoreLocations={changeLocation}
      deliveryAddressId={deliveryOrderDetail?.addressId}
    />
  );
};

export const ReorderConfirmLocationContent = (
  props: ReorderConfirmLocationContentProps,
) => {
  const { currentBreakpoint } = useResponsive();
  const Layout = currentBreakpoint.isMobile ? MobileLayout : DesktopLayout;
  const modalSize = currentBreakpoint.isMobile ? 'full' : 'medium';

  const addressId = props.deliveryAddressId ?? '';

  const [{ data: addressData }] = useAddressQuery({
    variables: { addressId },
    pause: !addressId,
  });

  if (props.fetching) {
    return (
      <TransparentModal
        testID="reorder-confirm-location"
        size={modalSize}
        onClose={props.cancelReorder}
        mobileEdges={['bottom']}
      >
        <View style={styles.loadingContainer}>
          <LoadingAnimation />
        </View>
      </TransparentModal>
    );
  }

  return (
    <TransparentModal
      size={modalSize}
      onClose={props.cancelReorder}
      mobileEdges={['bottom']}
    >
      <Layout {...props} deliveryAddress={addressData?.address}>
        <Map draggable={false} showControls={false} {...props} />
      </Layout>
    </TransparentModal>
  );
};

const DesktopLayout = ({
  orderType,
  restaurant,
  deliveryAddress,
  isLoading,
  children,
  onClose,
  onMoreLocations,
  onContinue,
}: Omit<ReorderConfirmLocationLayoutProps, 'navigation'>) => {
  const isDelivery = orderType === AddressTypes.delivery;

  return (
    <View
      testID="reordering.confirm-location.container"
      style={styles.desktopContainer}
    >
      <View style={styles.desktopMapContainer}>{children}</View>
      <View style={styles.desktopContentContainer}>
        <ModalHeader orderType={orderType} onClose={onClose} />
        <ModalRow>
          {isDelivery ? (
            <DeliveryLocationCard deliveryAddress={deliveryAddress} />
          ) : (
            <RestaurantLocationCard
              orderType={orderType}
              restaurant={restaurant}
            />
          )}
        </ModalRow>
        <ModalFooter
          isLoading={isLoading}
          onMoreLocations={onMoreLocations}
          onContinue={onContinue}
        />
      </View>
    </View>
  );
};

const MobileLayout = ({
  navigation,
  orderType,
  restaurant,
  deliveryAddress,
  isLoading,
  children,
  onMoreLocations,
  onContinue,
}: Omit<ReorderConfirmLocationLayoutProps, 'onClose'>) => {
  const { width } = useWindowDimensions();
  const dynamicHeight = useStyle(() => ({ height: width * 0.5 }), [width]);
  const isDelivery = orderType === AddressTypes.delivery;

  // Mobile Header
  const rightIcon = useReorderRightIcon();

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <ReorderTitle orderType={orderType} />,
      headerRight: () => rightIcon,
      headerShown: true,
    });
  }, [navigation, orderType, rightIcon]);

  return (
    <View
      testID="reordering.confirm-location.container"
      style={styles.mobileContainer}
    >
      <ScrollView
        testID="reordering.confirm-location.contents"
        style={styles.mobileContent}
        contentContainerStyle={styles.grow}
      >
        <ModalRow>
          <View style={[styles.mobileMapContainer, dynamicHeight]}>
            {children}
          </View>
        </ModalRow>
        <ModalRow style={styles.mobileLocationContainer}>
          {isDelivery ? (
            <DeliveryLocationCard deliveryAddress={deliveryAddress} />
          ) : (
            <RestaurantLocationCard
              orderType={orderType}
              restaurant={restaurant}
            />
          )}
        </ModalRow>
      </ScrollView>
      <ModalFooter
        isLoading={isLoading}
        onMoreLocations={onMoreLocations}
        onContinue={onContinue}
      />
    </View>
  );
};

const RestaurantLocationCard = ({
  restaurant,
  orderType,
}: RestaurantLocationCardProps) => {
  return (
    <LocationCard
      id={restaurant.id}
      acceptingOrders={restaurant.isAcceptingOrders}
      notAcceptingOrdersReason={restaurant.notAcceptingOrdersReason}
      restaurantSlug={restaurant.slug}
      address={restaurant.address}
      city={restaurant.city}
      state={restaurant.state}
      zipCode={restaurant.zipCode}
      name={restaurant.name}
      flexMessage={restaurant.flexMessage ?? ''}
      lat={restaurant.latitude}
      lng={restaurant.longitude}
      storeHours={restaurant.hours?.formatted ?? ''}
      imageUrl={orderType !== 'outpost' ? restaurant.asset?.url : undefined}
    />
  );
};

const DeliveryLocationCard = ({
  deliveryAddress,
}: DeliveryLocationCardProps) => {
  if (!deliveryAddress) return null;

  const deliveryAddressStreet = `${deliveryAddress?.street}${
    deliveryAddress?.secondaryStreet
      ? `, ${deliveryAddress?.secondaryStreet}`
      : ''
  }`;

  return (
    <LocationCard
      id={deliveryAddress?.id ?? ''}
      acceptingOrders={true}
      notAcceptingOrdersReason=""
      restaurantSlug=""
      address={deliveryAddressStreet}
      city={deliveryAddress?.city ?? ''}
      state={deliveryAddress?.state ?? ''}
      zipCode={deliveryAddress?.zipCode ?? ''}
      name={deliveryAddress?.name ?? ''}
      flexMessage=""
      lat={deliveryAddress?.latitude ?? 0}
      lng={deliveryAddress?.longitude ?? 0}
      storeHours=""
    />
  );
};

type ReorderConfirmLocationContentProps = {
  readonly fetching?: boolean;
  readonly deliveryAddressId?: string;
  readonly cancelReorder: () => void;
} & MapProps &
  ReorderConfirmLocationLayoutProps;

type ReorderConfirmLocationLayoutProps = {
  readonly navigation: ModalStackChildProps<'ReorderConfirmLocation'>['navigation'];
  readonly orderType: AddressType;
  readonly restaurant: ReorderRestaurantQuery['restaurant'];
  readonly isLoading?: boolean;
  readonly children?: ViewProps['children'];
  readonly deliveryAddress?: AddressQuery['address'];
  readonly onClose: () => void;
  readonly onMoreLocations: () => void;
  readonly onContinue: () => void;
};

type RestaurantLocationCardProps = {
  readonly restaurant: ReorderRestaurantQuery['restaurant'];
  readonly orderType: AddressType;
};

type DeliveryLocationCardProps = {
  readonly deliveryAddress?: AddressQuery['address'];
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    height: '100%',
    padding: theme.spacing.xxlarge,
    backgroundColor: theme.colors.OATMEAL,
  },
  desktopContainer: {
    minHeight: 504,
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.OATMEAL,
  },
  mobileContainer: {
    flex: 1,
    backgroundColor: theme.colors.OATMEAL,
  },
  mobileContent: {
    flex: 1,
  },
  grow: {
    flexGrow: 1,
  },
  desktopMapContainer: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
    width: '50%',
    height: '100%',
  },
  desktopContentContainer: {
    flex: 1,
    width: '50%',
    height: '100%',
    justifyContent: 'space-around',
  },
  mobileMapContainer: {
    height: 192,
    borderRadius: theme.radius.medium,
    borderWidth: 1,
    borderColor: theme.colors.NEUTRAL_3,
    overflow: 'hidden',
  },
  mobileLocationContainer: {
    paddingVertical: theme.spacing.medium,
  },
});
